import { useEffect, useMemo } from 'react';
import type { Product } from '@/features/products/types/products.types';
import type { CollectionTarget } from '../../../types/schedule.types';
import { deriveDepositBounds } from './depositBounds';
import { resolveProductDepositMinor, shouldDiscardProductDeposit } from './depositMode';
import type { DepositOrigin, ProductDeposit } from './depositMode';

export function useProductDeposit(selectedProduct: Product | null | undefined): ProductDeposit | undefined {
    const allowDeposit = selectedProduct?.allowDeposit;
    const defaultMinor = selectedProduct?.defaultDepositAmountMinor;
    const minMinor = selectedProduct?.minDepositAmountMinor;
    const maxMinor = selectedProduct?.maxDepositAmountMinor;

    return useMemo(
        () =>
            resolveProductDepositMinor({
                allowDeposit,
                defaultDepositAmountMinor: defaultMinor,
                minDepositAmountMinor: minMinor,
                maxDepositAmountMinor: maxMinor,
            }),
        [allowDeposit, defaultMinor, minMinor, maxMinor],
    );
}

export function useDepositCeiling(options: {
    isReadOnly: boolean;
    isDepositBearing: boolean;
    isItemBasedDepositBearing: boolean;
    isItemBasedFamily: boolean;
    collectionTargets: CollectionTarget[];
    isBasketDerived: boolean;
    isPricingUpdating: boolean;
    basketGrossMinor: number;
    maxDepositAmountMinor: number | undefined;
    effectiveDepositMinor: number | undefined;
    depositMinor: number | undefined;
    depositOrigin: DepositOrigin;
    isDepositFixed: boolean;
    discardProductDeposit: (judgedMinor: number) => void;
}): ReturnType<typeof deriveDepositBounds> & { previewDepositMinor: number | undefined } {
    const bounds = deriveDepositBounds({
        isDepositBearingMethod: options.isDepositBearing,
        isItemBasedDepositBearing: options.isItemBasedDepositBearing,
        isItemBasedFamily: options.isItemBasedFamily,
        isDepositFixed: options.isDepositFixed,
        collectionTargets: options.collectionTargets,
        basketGrossMinor: options.basketGrossMinor,
        maxDepositAmountMinor: options.maxDepositAmountMinor,
        depositMinor: options.effectiveDepositMinor,
    });

    const isBoundsSettled =
        options.isBasketDerived && (!options.isItemBasedDepositBearing || bounds.firstTargetMinor !== undefined);

    const productValueExceedsCeiling = shouldDiscardProductDeposit({
        isReadOnly: options.isReadOnly,
        isDepositBearing: options.isDepositBearing,
        isBoundsSettled,
        origin: options.depositOrigin,
        depositMinor: options.depositMinor,
        effectiveMaxExclusive: bounds.effectiveMaxExclusive,
    });
    // A refetch over cached pricing can still raise the ceiling, so the discard waits for it.
    const discardMinor =
        productValueExceedsCeiling && !options.isPricingUpdating ? options.depositMinor : undefined;

    const { discardProductDeposit } = options;
    useEffect(() => {
        if (discardMinor !== undefined) {
            discardProductDeposit(discardMinor);
        }
    }, [discardMinor, discardProductDeposit]);

    const withholdsProductValue = productValueExceedsCeiling && !options.isDepositFixed;
    const previewDepositMinor = withholdsProductValue ? undefined : options.effectiveDepositMinor;
    const depositExceedsEffectiveMax = bounds.depositExceedsEffectiveMax && !withholdsProductValue;

    return { ...bounds, depositExceedsEffectiveMax, previewDepositMinor };
}
